import { useCallback, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import * as zod from 'zod';
import { LggPopover } from 'src/components/general/display/popover';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { preventDefaultAndStopPropagation } from 'src/components/general/inputs/helpers';
import { Radio } from 'src/components/general/inputs/radio';
import { TextInput } from 'src/components/general/inputs/text-input';
import {
  PlaceholderOptionsOverlay,
  TemplatePlaceholderOverlayBody,
  TemplatePlaceholderOverlayContainer,
  TemplatePlaceholderOverlayFooter,
  TemplatePlaceholderOverlayHeader,
} from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';

type TemplatePresetPlaceholderProps = {
  children: React.ReactChild;
  fallbackValue?: string | null;
  onSubmit: (fallbackValue?: string) => void;
  onRemove: VoidFunction;
};

export const TemplatePresetPlaceholder = ({
  children,
  onSubmit,
  onRemove,
  fallbackValue,
}: TemplatePresetPlaceholderProps) => {
  const overlayVisibility = useVisible();

  return (
    <span>
      <PlaceholderOptionsOverlay onEdit={overlayVisibility.show} onRemove={onRemove}>
        {children}
      </PlaceholderOptionsOverlay>
      <TemplatePresetPlaceholderOverlay
        onSubmit={onSubmit}
        visible={overlayVisibility.visible}
        fallbackValue={fallbackValue}
        onClose={() => {
          overlayVisibility.close();
        }}
      />
    </span>
  );
};

const CustomTextEditorContainer = styled.div`
  margin-left: 30px;
  margin-top: 6px;
`;

const StyledTextInput = styled(TextInput)`
  &:disabled {
    background: ${({ theme }) => theme.colors.porcelain};
    border-color: ${({ theme }) => theme.colors.koala};

    &::placeholder {
      color: ${({ theme }) => theme.colors.casper};
    }
  }
`;

const InputContainer = styled.div`
  ${up('md')} {
    padding: 5px;
  }

  & > *:first-child {
    margin-bottom: 25px !important;
  }
`;

const StyledTemplatePlaceholderPopoverBody = styled(TemplatePlaceholderOverlayBody)`
  padding: 20px 20px 60px;

  ${up('md')} {
    padding: 10px;
  }
`;

const fallbackValueFormSchema = zod
  .object({
    fallbackType: zod.union([zod.literal('CUSTOM'), zod.literal('EMPTY')]),
    fallbackValue: zod.string().nullish(),
  })
  .required();

type TemplatePresetPlaceholderOverlayProps = {
  fallbackValue?: string | null;
  onSubmit: (fallbackValue?: string) => void;
  visible: boolean;
  onClose: VoidFunction;
};

export const TemplatePresetPlaceholderOverlay = ({
  fallbackValue = null,
  onSubmit,
  visible,
  onClose,
}: TemplatePresetPlaceholderOverlayProps) => {
  const { t } = useTranslation(['conversations']);
  const breakpointUpMd = useBreakpoint(up('md'));

  type FallbackFormValues = zod.infer<typeof fallbackValueFormSchema>;

  const form = useForm<FallbackFormValues>({
    resolver: zodResolver(fallbackValueFormSchema),
    defaultValues: {
      fallbackType: fallbackValue ? 'CUSTOM' : 'EMPTY',
      fallbackValue: fallbackValue ?? undefined,
    },
    shouldFocusError: true,
  });

  useEffect(() => {
    if (!visible) {
      form.reset();
    }
  }, [form, visible]);

  const onSubmitHandler = useCallback(async () => {
    await form.handleSubmit(({ fallbackType, fallbackValue }) => {
      if (fallbackType === 'CUSTOM' && (!fallbackValue || fallbackValue.length <= 0)) {
        form.setError('fallbackValue', { type: 'required' });

        return;
      }

      onSubmit(fallbackValue ?? undefined);
      onClose();
    })();
  }, [form, onClose, onSubmit]);

  const formContent = useMemo(
    () => (
      <TemplatePlaceholderOverlayContainer data-lgg-id="create-template-preset-placeholder-overlay">
        <TemplatePlaceholderOverlayHeader
          title={t(
            'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.presetPlaceholder.createPopover.title',
          )}
          subTitle={t(
            'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.presetPlaceholder.createPopover.subTitle',
          )}
        />
        <FormProvider {...form}>
          <form
            onSubmit={async (e) => {
              preventDefaultAndStopPropagation(e);

              await onSubmitHandler();
            }}
          >
            <StyledTemplatePlaceholderPopoverBody>
              <InputContainer>
                <Controller
                  control={form.control}
                  name="fallbackType"
                  render={({ field }) => {
                    return (
                      <Radio
                        checked={field.value === 'EMPTY'}
                        onChange={() => {
                          form.setValue('fallbackType', 'EMPTY');
                          form.setValue('fallbackValue', '');
                        }}
                        name="fallback-type-empty"
                        testId="fallback-type-empty"
                        text={t(
                          'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.presetPlaceholder.createPopover.inputs.leaveEmpty.label',
                        )}
                        value="EMPTY"
                      />
                    );
                  }}
                />
                <Controller
                  control={form.control}
                  name="fallbackType"
                  render={({ field }) => {
                    return (
                      <Radio
                        checked={field.value === 'CUSTOM'}
                        onChange={() => {
                          form.setValue('fallbackType', 'CUSTOM');
                        }}
                        name="fallback-type-custom"
                        testId="fallback-type-custom"
                        text={`${t(
                          'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.presetPlaceholder.createPopover.inputs.custom.label',
                        )}:`}
                        value="CUSTOM"
                      />
                    );
                  }}
                />
                <CustomTextEditorContainer>
                  <Controller
                    control={form.control}
                    name="fallbackType"
                    render={({ field: fallbackTypeField }) => {
                      return (
                        <Controller
                          control={form.control}
                          defaultValue={null}
                          name="fallbackValue"
                          rules={{
                            required: fallbackTypeField.value === 'CUSTOM',
                          }}
                          render={({ field, fieldState }) => (
                            <StyledTextInput
                              {...field}
                              value={field.value ?? ''}
                              label=""
                              placeholder={t(
                                'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.presetPlaceholder.createPopover.inputs.custom.placeholder',
                              )}
                              autoComplete="off"
                              data-lgg-id="fallback-type-custom-input"
                              disabled={fallbackTypeField.value !== 'CUSTOM'}
                              error={Boolean(fieldState.error)}
                              showErrorMessage={false}
                              reserveErrorArea={false}
                            />
                          )}
                        />
                      );
                    }}
                  />
                </CustomTextEditorContainer>
              </InputContainer>
            </StyledTemplatePlaceholderPopoverBody>
            <TemplatePlaceholderOverlayFooter onClose={onClose} />
          </form>
        </FormProvider>
      </TemplatePlaceholderOverlayContainer>
    ),
    [form, onClose, onSubmitHandler, t],
  );

  return breakpointUpMd ? (
    <LggPopover
      content={formContent}
      hideArrow
      placement="topLeft"
      trigger={['click']}
      destroyTooltipOnHide
      visible={visible}
      onVisibleChange={(isVisible) => {
        if (!isVisible) {
          onClose();
        }
      }}
    >
      <span></span>
    </LggPopover>
  ) : (
    <BottomDrawer
      visible={visible}
      destroyOnClose
      title={t(
        'conversations:messageInput.options.messageTemplates.editPlaceholderOverlay.titleShort',
      )}
      onClose={onClose}
    >
      {formContent}
    </BottomDrawer>
  );
};
